/**
 * Autosave & Crash Recovery
 *
 * A lightweight safety net on top of the document store: while the app runs,
 * a dirty document periodically writes the FULL .yap/.yate envelope to a
 * real autosave file — `yap/autosave/<docName>.sv$` under the Tauri appData
 * dir on the desktop host, mirrored into the Origin Private File System in
 * the browser (Faz 9.2). There is no size cap: the old 3.5M localStorage
 * limit is gone and huge envelopes stream in 1 MiB chunks (desktopIo).
 *
 * A clean Save / Open / New deletes the document's .sv$; if the tab or
 * process dies without one, the next launch finds the leftover file and
 * offers to restore it (RecoveryHost). Legacy localStorage snapshots from
 * older builds are still read once for migration.
 *
 * `startAutosave` is wired by the App bootstrap; the write callback is
 * injected so ProjectFileIO can build the envelope without a circular
 * dependency.
 */

import { useDocumentStore } from '../state/useDocumentStore';
import { useSystemVarsStore } from '../state/useSystemVarsStore';
import {
    AUTOSAVE_DIR,
    deleteAppFile,
    listAppFiles,
    readAppFile,
    writeAppFile,
} from '../services/fileSystem/desktopIo';

/** 30 s base tick — the real cadence is governed by the SAVETIME system
 *  variable (minutes; 0 = autosave disabled), checked every tick. */
const AUTOSAVE_TICK_MS = 30_000;
const AUTOSAVE_EXT = '.sv$';
const LEGACY_SNAPSHOT_KEY = '***';

export interface AutosaveSnapshot {
    displayName: string;
    kind: 'project' | 'template';
    savedAt: string;
    envelopeText: string;
}

/**
 * Outer file body of a .sv$ autosave: wraps the full .yap envelope so the
 * restore path hands the inner envelope text to the regular load pipeline
 * (full-fidelity recovery — entity/layout/block sets come back verbatim).
 */
interface AutosaveFileBody {
    format: 'yap-autosave';
    version: 1;
    appName: 'Yap';
    displayName: string;
    kind: AutosaveSnapshot['kind'];
    savedAt: string;
    envelope: unknown;
}

/** File-system-safe document name for the sv$ target (OCS safe_name). */
function sanitizeDocName(name: string): string {
    const safe = (name || 'Untitled').replace(/[<>:"/\\|?*]/g, '_').trim();
    return safe || 'Untitled';
}

function autosavePathFor(displayName: string): string {
    return `${AUTOSAVE_DIR}/${sanitizeDocName(displayName)}${AUTOSAVE_EXT}`;
}

/** The path the last write touched — the clean-save delete target. */
let lastWrittenPath: string | null = null;

/** Writes the document's .sv$ (desktop: atomic tmp→.bak→rename in Rust). */
export async function writeAutosaveFile(snapshot: AutosaveSnapshot): Promise<void> {
    let envelope: unknown;
    try {
        envelope = JSON.parse(snapshot.envelopeText);
    } catch {
        return; // Never write a half-parseable recovery file.
    }
    const body: AutosaveFileBody = {
        format: 'yap-autosave',
        version: 1,
        appName: 'Yap',
        displayName: snapshot.displayName,
        kind: snapshot.kind,
        savedAt: snapshot.savedAt,
        envelope,
    };
    const path = autosavePathFor(snapshot.displayName);
    await writeAppFile(path, JSON.stringify(body), true);
    lastWrittenPath = path;
}

/** Parses an autosave file body (or a legacy localStorage JSON) back. */
function parseAutosaveBody(raw: string): AutosaveSnapshot | null {
    try {
        const parsed = JSON.parse(raw) as Partial<AutosaveFileBody> & Partial<AutosaveSnapshot>;
        if (parsed.format === 'yap-autosave' && typeof parsed.envelope === 'object' && parsed.envelope !== null
            && typeof parsed.displayName === 'string' && typeof parsed.savedAt === 'string') {
            return {
                displayName: parsed.displayName,
                kind: parsed.kind === 'template' ? 'template' : 'project',
                savedAt: parsed.savedAt,
                envelopeText: JSON.stringify(parsed.envelope),
            };
        }
        // Legacy localStorage snapshot shape (pre-Faz-9 builds).
        if (typeof (parsed as AutosaveSnapshot).envelopeText === 'string'
            && typeof (parsed as AutosaveSnapshot).displayName === 'string') {
            return parsed as AutosaveSnapshot;
        }
        return null;
    } catch {
        return null;
    }
}

/** Newest leftover autosave across the autosave folder, if any. */
export async function getRecoverySnapshot(): Promise<AutosaveSnapshot | null> {
    let newest: AutosaveSnapshot | null = null;
    try {
        const names = (await listAppFiles(AUTOSAVE_DIR)).filter((n) => n.endsWith(AUTOSAVE_EXT));
        for (const name of names) {
            const raw = await readAppFile(`${AUTOSAVE_DIR}/${name}`);
            if (!raw) continue;
            const snap = parseAutosaveBody(raw);
            if (snap && (!newest || snap.savedAt > newest.savedAt)) newest = snap;
        }
    } catch {
        // Storage unavailable: fall through to the legacy slot.
    }
    if (newest) return newest;
    try {
        const raw = localStorage.getItem(LEGACY_SNAPSHOT_KEY);
        return raw ? parseAutosaveBody(raw) : null;
    } catch {
        return null;
    }
}

/** Deletes the given document's autosave file (best-effort, both hosts).
 *  With no name, falls back to the path the last write touched. */
export async function clearAutosaveFile(displayName?: string): Promise<void> {
    const targets = new Set<string>();
    if (displayName) targets.add(autosavePathFor(displayName));
    if (lastWrittenPath) targets.add(lastWrittenPath);
    for (const path of targets) {
        try {
            await deleteAppFile(path);
        } catch {
            // ignore — best-effort cleanup
        }
    }
    lastWrittenPath = null;
    try {
        localStorage.removeItem(LEGACY_SNAPSHOT_KEY);
    } catch {
        // ignore
    }
}

let timer: number | null = null;
let lastSnapshotAt = 0;

/** Starts the periodic dirty-document snapshot (idempotent). The SAVETIME
 *  system variable (minutes) drives the cadence; 0 suspends snapshots. */
export function startAutosave(write: () => Promise<void>): void {
    if (timer !== null) return;
    timer = window.setInterval(() => {
        if (!useDocumentStore.getState().isDirty) return;
        const minutes = useSystemVarsStore.getState().SAVETIME ?? 10;
        if (minutes <= 0) return;
        if (Date.now() - lastSnapshotAt < minutes * 60_000) return;
        lastSnapshotAt = Date.now();
        void write();
    }, AUTOSAVE_TICK_MS);
}

// A document becoming clean means it was saved (or discarded/replaced by a
// load) — in every case the recovery .sv$ is obsolete. Watching the dirty
// transition centrally covers all save/load paths without each flow
// remembering to clear the slot.
if (typeof window !== 'undefined') {
    let wasDirty = useDocumentStore.getState().isDirty;
    useDocumentStore.subscribe((state) => {
        if (wasDirty && !state.isDirty) void clearAutosaveFile(state.displayName);
        wasDirty = state.isDirty;
    });
}
